"use client";

import { useTranslation } from "react-i18next";
import { useCurrency } from "@/app/components/providers/CurrencyProvider";
import { formatPrice, getPriceForPlan } from "@/app/utils/currency-config";
import { AnimatedIcon } from "../AnimatedIcon";
import { KMU_ICONS } from "../animatedIcons";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";

export const KMU_BENEFIT_COUNT = 4;

export default function BuiltForKMUSignal() {
  const { t } = useTranslation();
  const { currency } = useCurrency();

  const priceInterpolation = {
    price: formatPrice(getPriceForPlan(currency, "lite"), currency),
    bigBudget: formatPrice(50000, currency),
  };

  const benefits = Array.from({ length: KMU_BENEFIT_COUNT }, (_, index) => ({
    title: t(`landing.builtForKMU.benefit${index + 1}.title`),
    description: t(
      `landing.builtForKMU.benefit${index + 1}.description`,
      priceInterpolation
    ),
  }));

  return (
    <section
      aria-labelledby="kmu-title"
      className="relative overflow-hidden py-20 lg:py-28"
    >
      <div className="relative mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8">
        <div className="lg:col-span-4">
          <Reveal className="lg:sticky lg:top-32">
            <SectionHeading
              id="kmu-title"
              align="left"
              eyebrow={t("landing.builtForKMU.badge", "")}
              title={t("landing.builtForKMU.title")}
            />
            <p className="mt-6 max-w-[44ch] text-pretty text-[17px] leading-[1.65] text-signal-muted">
              {t("landing.builtForKMU.subtitle", priceInterpolation)}
            </p>
          </Reveal>
        </div>

        <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:col-span-8 lg:gap-7">
          {benefits.map((benefit, index) => (
            <Reveal as="li" key={benefit.title} delay={index * 80}>
              <div className="glass group h-full rounded-glass p-7 transition-colors duration-300 hover:bg-white/30 sm:p-9">
                <span
                  aria-hidden="true"
                  className="glass-inset flex h-12 w-12 items-center justify-center rounded-full text-signal-deep"
                >
                  {/* Two-column stagger, as on heyfinn.ai */}
                  <AnimatedIcon
                    icon={KMU_ICONS[index]}
                    className="h-7 w-7"
                    host="li"
                    playOnView={
                      250 + (index % 2) * 140 + Math.floor(index / 2) * 90
                    }
                  />
                </span>
                <h3 className="mt-6 font-display text-[21px] font-semibold tracking-[-0.02em] text-signal-ink">
                  {benefit.title}
                </h3>
                <p className="mt-3 text-pretty text-[15px] leading-relaxed text-signal-muted">
                  {benefit.description}
                </p>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}
